import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Cart.css';
import { useAuth } from '../../contexts/AuthContext';
import { getCartItems, removeCartItem, updateCartItem } from '../../firebase/Cart';
import { getShirtSizePrices, FALLBACK_SHIRT_SIZE_PRICES } from '../../firebase/products';
import ShirtPreviewModal from '../ShirtPreviewModal/ShirtPreviewModal';

// ─── Subcategory constants ────────────────────────────────────────────────────
const SUBCATEGORY_TSHIRT = 'T-Shirt';
const SUBCATEGORY_JERSEY = 'Jersey';
const SUBCATEGORY_POLO   = 'Polo Shirt';

// ─── Helpers: detect product type ────────────────────────────────────────────
function isJerseyItem(item) {
    return item?.subcategory === SUBCATEGORY_JERSEY;
}
function isPoloItem(item) {
    return item?.subcategory === SUBCATEGORY_POLO;
}
function isPoloOrJersey(item) {
    return isJerseyItem(item) || isPoloItem(item);
}

// ─── Size / Type constants ────────────────────────────────────────────────────
const SIZE_OPTIONS = [
    { key: 'xxs',    label: 'XXS'   },
    { key: 'xs',     label: 'XS'    },
    { key: 'small',  label: 'Small' },
    { key: 'medium', label: 'Medium'},
    { key: 'large',  label: 'Large' },
    { key: 'xl',     label: 'XL'    },
    { key: 'xxl',    label: '2XL'   },
    { key: 'xxxl',   label: '3XL'   },
    { key: 'xxxxl',  label: '4XL'   },
    { key: 'xxxxxl', label: '5XL'   },
];
const ALL_SIZES = SIZE_OPTIONS.map(o => o.key);

const TYPE_SIZE_MAPPING = {
    'Blue Corner':      ['xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl'],
    'Yalex':            ['xs','small','medium','large','xl','xxl','xxxl','xxxxxl'],
    'Pro Club Inspire': ['small','medium','large','xl','xxl'],
    'Blackhorse':       ['xs','small','medium','large','xl','xxl'],
    'Active Life':      ['xs','small','medium','large','xl','xxl','xxxl','xxxxxl'],
    'Drift':            ['xs','small','medium','large','xl','xxl'],
};

// ─── Imprint pricing ──────────────────────────────────────────────────────────
const IMPRINT_PRICES_RECTANGLE = {
    'Full Front':150, 'Medium Front':120, 'Center Chest':50, 'Across Chest':80,
    'Left Chest':50, 'Right Chest':50, 'Left Vertical':120, 'Right Vertical':120,
    'Bottom Left':50, 'Bottom Right':50, 'Full Back':150, 'Medium Back':120,
    'Locker Center Patch':50, 'Across Shoulder':80, 'Left Sleeve':40, 'Right Sleeve':40,
};
const IMPRINT_PRICES_CIRCLE = {
    'Full Front':120, 'Medium Front':80, 'Center Chest':50, 'Across Chest':80,
    'Left Chest':40, 'Right Chest':40, 'Left Vertical':100, 'Right Vertical':100,
    'Bottom Left':50, 'Bottom Right':50, 'Full Back':120, 'Medium Back':80,
    'Locker Center Patch':50, 'Back Center':80, 'Left Sleeve':40, 'Right Sleeve':40,
};

function calcImprintSubtotal(printLocation = '', designShape = '') {
    if (!printLocation) return 0;
    const shape  = designShape.toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table  = shape === 'circle' ? IMPRINT_PRICES_CIRCLE : IMPRINT_PRICES_RECTANGLE;
    const locs   = printLocation.split(',').map(s => s.trim()).filter(Boolean);
    if (locs.length === 0) return 0;
    const prices = locs.map(loc => table[loc] || 0).sort((a, b) => b - a);
    return prices.reduce((sum, price, idx) => sum + (idx === 0 ? price : Math.round(price * 0.5)), 0);
}

// ─── FIX: calcShirtSubtotal now prefers item.sizePrices over the lookup table ─
// This ensures the color surcharge and correct size prices are always used,
// even when the type name doesn't exactly match a key in sizePricesTable.
function calcShirtSubtotal(item, quantities, sizePricesTable = {}) {
    // Prefer sizePrices stored on the cart item itself (saved at add-to-cart time).
    // Fall back to the Firebase-fetched table only for older items that predate this fix.
    const sizePrices = (item.sizePrices && Object.keys(item.sizePrices).length > 0)
        ? item.sizePrices
        : (sizePricesTable[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);
    const qtyFromSizes = ALL_SIZES.reduce((s, sz) => s + (quantities[sz] || 0), 0);
    
    if (qtyFromSizes === 0) {
        return (Number(item.quantity) || 0) * ((Number(item.price) || 0) + colorSurcharge);
    }
    
    return ALL_SIZES.reduce(
        (sum, size) => {
            const price = Number(sizePrices[size]) || Number(item.price) || 0;
            return sum + (quantities[size] || 0) * (price + colorSurcharge);
        },
        0
    );
}

function calcPoloJerseyTotal(item, quantities) {
    const storedSizePrices = item.sizePrices || {};
    const hasSizePrices    = Object.keys(storedSizePrices).length > 0;
    const qtyFromSizes     = ALL_SIZES.reduce((s, sz) => s + (quantities?.[sz] || 0), 0);
    
    if (qtyFromSizes === 0) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }

    return ALL_SIZES.reduce((sum, sz) => {
        const qty = quantities?.[sz] || 0;
        if (qty === 0) return sum;
        const price = hasSizePrices
            ? (Number(storedSizePrices[sz]) || Number(item.price) || 0)
            : (Number(item.price) || 0);
        return sum + qty * price;
    }, 0);
}

function calcItemTotal(item, quantities, sizePricesTable = {}) {
    const qtyFromSizes = ALL_SIZES.reduce((s, sz) => s + (quantities?.[sz] || 0), 0);
    const qty = qtyFromSizes > 0 ? qtyFromSizes : (Number(item.quantity) || 0);
    if (qty === 0) return 0;
    
    if (isPoloOrJersey(item)) {
        return calcPoloJerseyTotal(item, quantities);
    }
    const shirtTotal   = calcShirtSubtotal(item, quantities, sizePricesTable);
    const imprintPerPc = calcImprintSubtotal(item.printLocation, item.designShape);
    return shirtTotal + imprintPerPc * qty;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getBrandFromType(type = '') {
    const brands = ['Blue Corner','Yalex','Pro Club Inspire','Blackhorse','Active Life','Drift'];
    return brands.find(b => type.startsWith(b)) || '';
}

function getAvailableSizesForItem(item) {
    if (isPoloOrJersey(item)) {
        const orderedSizes = ALL_SIZES.filter(sz => (item.quantities?.[sz] || 0) > 0);
        if (orderedSizes.length === 0) {
            const sizePriceKeys = Object.keys(item.sizePrices || {});
            return sizePriceKeys.length > 0 ? sizePriceKeys : ALL_SIZES;
        }
        return ALL_SIZES;
    }
    const brand = getBrandFromType(item.type || '');
    return TYPE_SIZE_MAPPING[brand] || ALL_SIZES;
}

function filterQtyByType(item, quantities = {}) {
    if (isPoloOrJersey(item)) {
        return ALL_SIZES.reduce((acc, sz) => {
            acc[sz] = parseInt(quantities[sz], 10) || 0;
            return acc;
        }, {});
    }
    const available = new Set(getAvailableSizesForItem(item));
    return ALL_SIZES.reduce((acc, sz) => {
        acc[sz] = available.has(sz) ? (parseInt(quantities[sz], 10) || 0) : 0;
        return acc;
    }, {});
}

function buildSizeBreakdown(quantities = {}) {
    return SIZE_OPTIONS
        .filter(s => (quantities[s.key] || 0) > 0)
        .map(s => `${s.label}: ${quantities[s.key]}`)
        .join(', ') || 'None';
}

// ─── Color display helper ─────────────────────────────────────────────────────
const COLOR_HEX_MAP = {
    'black':'#1f1f1f', 'red':'#EF4444', 'yellow':'#FCD34D', 'orange':'#F97316',
    'white':'#FFFFFF', 'blue':'#1a5faa', 'green':'#2e7d32', 'navy':'#1a237e',
    'gray':'#888888', 'pink':'#e91e8c', 'purple':'#7b1fa2', 'brown':'#5d4037',
};

function ColorSwatch({ colorKey }) {
    if (!colorKey) return null;
    const hex = COLOR_HEX_MAP[colorKey.toLowerCase()] || '#cccccc';
    const isLight = ['white', 'yellow'].includes(colorKey.toLowerCase());
    return (
        <span
            style={{
                display: 'inline-block',
                width: 14, height: 14, borderRadius: '50%',
                backgroundColor: hex,
                border: isLight ? '1.5px solid #ccc' : 'none',
                marginRight: 5, verticalAlign: 'middle',
                position: 'relative', top: -1,
            }}
        />
    );
}

// ─── Product image thumbnail ──────────────────────────────────────────────────
function ProductImageThumb({ item, onClick, isClickable }) {
    const imgSrc = item.imageUrl || item.image || null;
    return (
        <div
            className={`shirt-preview-wrap${isClickable ? ' clickable' : ''}`}
            style={{ position:'relative', background:'#f5f5f5', borderRadius:'8px', overflow:'hidden', cursor: isClickable ? 'pointer' : 'default' }}
            onClick={onClick}
            aria-label={item.name}
        >
            {imgSrc ? (
                <img
                    src={imgSrc} alt={item.name}
                    style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                />
            ) : null}
            <div className="shirt-preview-no-design" style={{ display: imgSrc ? 'none' : 'flex' }}>
                <span>No image</span>
            </div>
            {isClickable && <div className="preview-expand-icon" title="Preview design">🔍</div>}
        </div>
    );
}

function Cart() {
    const navigate        = useNavigate();
    const { currentUser } = useAuth();

    const [cartItems,         setCartItems]         = useState([]);
    const [loading,           setLoading]           = useState(true);
    const [selectedItems,     setSelectedItems]     = useState(new Set());
    const [showSizeBreakdown, setShowSizeBreakdown] = useState(null);
    const [editedQuantities,  setEditedQuantities]  = useState({});
    const [savingItemId,      setSavingItemId]      = useState(null);
    const [lightbox,          setLightbox]          = useState(null);
    const [shirtSizePrices,   setShirtSizePrices]   = useState(FALLBACK_SHIRT_SIZE_PRICES);
    const [previewModalItem,  setPreviewModalItem]  = useState(null);
    const [cartError,         setCartError]         = useState(null);

    useEffect(() => {
        let mounted = true;
        getShirtSizePrices().then(({ sizePrices, fromCache }) => {
            if (!mounted || fromCache) return;
            if (sizePrices && Object.keys(sizePrices).length > 0) setShirtSizePrices(sizePrices);
        });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const fetchCart = async () => {
            if (!currentUser) { setCartItems([]); setLoading(false); return; }
            setLoading(true);
            const { items, error } = await getCartItems(currentUser.uid);
            if (error) console.error('Error loading cart:', error);
            else setCartItems(items);
            setLoading(false);
        };
        fetchCart();
    }, [currentUser]);

    const toggleItemSelection = (id) => {
        setSelectedItems(prev => {
            const s = new Set(prev);
            s.has(id) ? s.delete(id) : s.add(id);
            return s;
        });
    };

    const toggleSelectAll = () => {
        setSelectedItems(
            selectedItems.size === cartItems.length && cartItems.length > 0
                ? new Set()
                : new Set(cartItems.map(i => i.id))
        );
    };

    const handleRemoveItem = async (firestoreId) => {
        setCartItems(prev => prev.filter(item => item.id !== firestoreId));
        setSelectedItems(prev => { const s = new Set(prev); s.delete(firestoreId); return s; });
        const { error } = await removeCartItem(firestoreId);
        if (error) console.error('Failed to remove item:', error);
    };

    const handleDeleteSelected = async () => {
        for (const id of Array.from(selectedItems)) await handleRemoveItem(id);
        setSelectedItems(new Set());
    };

    const handleSizeQuantityChange = (itemId, size, value) => {
        const val = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
        setEditedQuantities(prev => ({
            ...prev,
            [itemId]: { ...(prev[itemId] || {}), [size]: val },
        }));
    };

    const cancelSizeEdits = () => {
        const itemId = showSizeBreakdown;
        setShowSizeBreakdown(null);
        if (itemId) {
            setEditedQuantities(prev => {
                const updated = { ...prev };
                delete updated[itemId];
                return updated;
            });
        }
    };

    const closeAndSaveEdits = async () => {
        const itemId = showSizeBreakdown;
        setShowSizeBreakdown(null);

        if (itemId && editedQuantities[itemId]) {
            const cartItem = cartItems.find(i => i.id === itemId);
            if (cartItem) {
                const merged    = { ...cartItem.quantities, ...editedQuantities[itemId] };
                const sanitized = filterQtyByType(cartItem, merged);
                const total     = ALL_SIZES.reduce((s, sz) => s + (sanitized[sz] || 0), 0);
                const breakdown = buildSizeBreakdown(sanitized);

                setCartItems(prev => prev.map(item =>
                    item.id === itemId
                        ? { ...item, quantities: sanitized, quantity: total, sizeBreakdown: breakdown }
                        : item
                ));
                setEditedQuantities(prev => {
                    const updated = { ...prev };
                    delete updated[itemId];
                    return updated;
                });

                setSavingItemId(itemId);
                const { error } = await updateCartItem(itemId, {
                    quantities: sanitized, quantity: total, sizeBreakdown: breakdown,
                });
                setSavingItemId(null);
                if (error) console.error('Failed to save size changes to Firestore:', error);
            }
        }
    };

    const getLiveQuantities = (item) => {
        if (!item) return {};
        const edits = editedQuantities[item.id] || {};
        return filterQtyByType(item, { ...item.quantities, ...edits });
    };

    const getSizePricesForModal = (item) => {
        if (isPoloOrJersey(item)) return item.sizePrices || {};
        // FIX: Prefer item.sizePrices over the fetched table
        return (item.sizePrices && Object.keys(item.sizePrices).length > 0)
            ? item.sizePrices
            : (shirtSizePrices[item.type] || {});
    };

    const getItemTotal = (item) => {
        const liveQty = editedQuantities[item.id]
            ? getLiveQuantities(item)
            : (item.quantities || {});
        return calcItemTotal(item, liveQty, shirtSizePrices);
    };

    const calculateSelectedTotal = () =>
        cartItems.filter(i => selectedItems.has(i.id)).reduce((s, i) => s + getItemTotal(i), 0);

    const selectedItemCount = selectedItems.size;
    const totalItemCount    = cartItems.length;
    const activeCartItem    = showSizeBreakdown ? cartItems.find(i => i.id === showSizeBreakdown) : null;
    const activeItemSizes   = activeCartItem ? getAvailableSizesForItem(activeCartItem) : ALL_SIZES;

    const getPrintFileButtons = (item) => {
        const imgs = item.designImages;
        if (!imgs || typeof imgs !== 'object') return [];
        return Object.entries(imgs)
            .filter(([, url]) => url && typeof url === 'string')
            .map(([loc, url]) => ({ loc, url }));
    };

    const handleViewOrderDetails = () => {
        const selectedCartItems = cartItems.filter(item => selectedItems.has(item.id));
        const totalQuantity = selectedCartItems.reduce((sum, item) => {
            const qty = editedQuantities[item.id]
                ? Object.values(editedQuantities[item.id] || {}).reduce((s, q) => s + (q || 0), 0)
                : Number(item.quantity || 0);
            return sum + qty;
        }, 0);
        
        if (totalQuantity === 0) {
            setCartError('Your selected items have a total quantity of 0. Please increase the quantity before proceeding.');
            return;
        }

        navigate('/order-review', {
            state: {
                selectedCartItems,
                breadcrumbTrail: [
                    { label: 'Home',         path: '/'             },
                    { label: 'Cart',         path: '/cart'         },
                    { label: 'Order Review', path: '/order-review' },
                ],
            },
        });
    };

    if (loading) {
        return (
            <div className="cart-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading your cart...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-page">

            {lightbox && (
                <div className="design-lightbox-overlay" onClick={() => setLightbox(null)}>
                    <div className="design-lightbox" onClick={e => e.stopPropagation()}>
                        <div className="lightbox-header">
                            <span className="lightbox-loc-label">{lightbox.label}</span>
                            <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
                        </div>
                        <img src={lightbox.url} alt={lightbox.label} />
                    </div>
                </div>
            )}

            <ShirtPreviewModal
                open={!!previewModalItem}
                onClose={() => setPreviewModalItem(null)}
                designImages={previewModalItem?.designImages || {}}
                isCircle={(previewModalItem?.designShape || '').toLowerCase() === 'circle'}
                shirtColor={previewModalItem?.color || ''}
                title={`Preview — ${previewModalItem?.name || 'Item'}`}
                hideBackButton={true}
            />

            <div className="cart-container">
                {cartError && (
                    <div className="cart-error-banner" onClick={() => setCartError(null)}>
                        {cartError}
                    </div>
                )}
                <div className="cart-header-section">
                    <h1 className="cart-title">Cart</h1>
                    <button className="continue-shopping-btn" onClick={() => navigate('/')}>
                        <span className="back-arrow">←</span> Continue Shopping
                    </button>
                </div>

                {cartItems.length === 0 ? (
                    <div className="empty-cart"><p>Your cart is empty</p></div>
                ) : (
                    <>
                        <div className="cart-table">
                            <div className="cart-table-header">
                                <div className="header-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.size === cartItems.length && cartItems.length > 0}
                                        onChange={toggleSelectAll}
                                        aria-label="Select all items"
                                    />
                                </div>
                                <div className="header-product">Product</div>
                                <div className="header-quantity">Quantity</div>
                                <div className="header-print">Print File</div>
                                <div className="header-total">Total</div>
                            </div>

                            {cartItems.map(item => {
                                const printBtns      = getPrintFileButtons(item);
                                const itemTotal      = getItemTotal(item);
                                const isSelected     = selectedItems.has(item.id);
                                const isSaving       = savingItemId === item.id;
                                const isPoloJersey   = isPoloOrJersey(item);
                                const isJersey       = isJerseyItem(item);
                                const isPolo         = isPoloItem(item);
                                const tshirtHasDesigns = !isPoloJersey && printBtns.length > 0;
                                const colorSurcharge = !isPoloJersey ? Number(item.colorSurcharge || 0) : 0;

                                return (
                                    <div key={item.id} className={`cart-item ${isSelected ? 'is-selected' : ''}`}>

                                        <input
                                            type="checkbox"
                                            className="item-checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleItemSelection(item.id)}
                                            aria-label={`Select ${item.name}`}
                                        />

                                        {/* Product Info */}
                                        <div className="item-product">
                                            <ProductImageThumb
                                                item={item}
                                                isClickable={tshirtHasDesigns}
                                                onClick={tshirtHasDesigns ? () => setPreviewModalItem(item) : undefined}
                                            />

                                            <div className="item-details">
                                                <h3 className="item-name">{item.name}</h3>

                                                {!isJersey && (
                                                    <p className="item-spec">
                                                        <span className="spec-label">Type:</span> {item.type || item.name}
                                                    </p>
                                                )}
                                                {isJersey && item.type && (
                                                    <p className="item-spec">
                                                        <span className="spec-label">Type:</span> {item.type}
                                                    </p>
                                                )}

                                                {/* T-Shirt only: Color (with surcharge note), Design Shape, Print Location */}
                                                {!isPoloJersey && (
                                                    <>
                                                        <p className="item-spec">
                                                            <span className="spec-label">Color:</span>{' '}
                                                            <ColorSwatch colorKey={item.color} />
                                                            {item.color
                                                                ? item.color.charAt(0).toUpperCase() + item.color.slice(1)
                                                                : '—'}
                                                            {colorSurcharge > 0 && (
                                                                <span style={{ marginLeft: 5, fontSize: 11, color: '#888' }}>
                                                                    (+₱{colorSurcharge}/pc)
                                                                </span>
                                                            )}
                                                        </p>
                                                        <p className="item-spec"><span className="spec-label">Design Shape:</span> {item.designShape}</p>
                                                        <p className="item-spec"><span className="spec-label">Print Location:</span> {item.printLocation}</p>
                                                    </>
                                                )}

                                                {/* Jersey-specific fields */}
                                                {isJersey && (
                                                    <>
                                                        {item.teamName && <p className="item-spec"><span className="spec-label">Team Name:</span> {item.teamName}</p>}
                                                        {item.surname  && <p className="item-spec"><span className="spec-label">Surname:</span> {item.surname}</p>}
                                                        {item.number   && <p className="item-spec"><span className="spec-label">Number:</span> {item.number}</p>}
                                                    </>
                                                )}

                                                {isPolo && (
                                                    <p className="item-spec cart-polo-design-note">
                                                        <span className="spec-label">Design:</span> Provided by JG Printing
                                                    </p>
                                                )}

                                                {item.note && (
                                                    <p className="item-spec"><span className="spec-label">Note:</span> {item.note}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Quantity */}
                                        <div className="item-quantity">
                                            <div className="quantity-display">
                                                {(() => { const sizeQty = ALL_SIZES.reduce((s, sz) => s + ((editedQuantities[item.id]?.[sz] ?? item.quantities?.[sz]) || 0), 0); return sizeQty > 0 ? sizeQty : (Number(item.quantity) || 0); })()}
                                            </div>
                                        </div>

                                        {/* Print File */}
                                        <div className="item-print">
                                            {isPoloJersey ? (
                                                <div className="cart-no-design-badge">
                                                    <span className="cart-no-design-icon">🎨</span>
                                                    <span className="cart-no-design-text">
                                                        Design is not available for this product
                                                    </span>
                                                </div>
                                            ) : printBtns.length > 0 ? (
                                                printBtns.map(({ loc, url }) => (
                                                    <button
                                                        key={loc}
                                                        className="print-file-btn"
                                                        onClick={() => setLightbox({ url, label: loc })}
                                                        title={`View ${loc}`}
                                                    >
                                                        <img src={url} alt={loc} className="print-file-thumb" />
                                                        <span className="print-btn-label">{loc}</span>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="print-btn-icon">
                                                            <circle cx="11" cy="11" r="8"/>
                                                            <path d="M21 21l-4.35-4.35"/>
                                                        </svg>
                                                    </button>
                                                ))
                                            ) : (
                                                <span className="no-design-note">No designs uploaded</span>
                                            )}
                                        </div>

                                        {/* Total */}
                                        <div className="item-total">
                                            <span>₱ {itemTotal.toLocaleString()}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>



                        {/* Cart Footer */}
                        <div className="cart-footer">
                            <div className="order-summary-box">
                                <h2 className="summary-title">Order Summary</h2>
                                <p className="summary-text">
                                    Please review your order details and print design before approval.
                                    Select items and click "View Order Details" in the bar below to proceed.
                                </p>
                            </div>

                            <div className="cart-total-box">
                                <div className="total-items">
                                    {selectedItemCount > 0
                                        ? <><strong>{selectedItemCount}</strong> {selectedItemCount === 1 ? 'item' : 'items'} selected out of {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}</>
                                        : <>0 items selected out of {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'}</>
                                    }
                                </div>
                                <div className="total-divider" />
                                <div className="total-amount">
                                    <span className="total-label">Selected Total:</span>
                                    <span className="total-price">₱ {calculateSelectedTotal().toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Sticky selection bar */}
            {selectedItemCount > 0 && (
                <div className="selection-sticky-bar">
                    <span className="selection-count">
                        {selectedItemCount} {selectedItemCount === 1 ? 'item' : 'items'} selected
                        &nbsp;·&nbsp;
                        <strong>₱ {calculateSelectedTotal().toLocaleString()}</strong>
                    </span>
                    <div className="selection-actions">
                        <button className="selection-delete-btn" onClick={handleDeleteSelected}>
                            🗑 Delete Selected
                        </button>
                        <button className="selection-order-btn" onClick={handleViewOrderDetails}>
                            View Order Details →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Cart;